import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import WaveSurfer from 'wavesurfer.js'
// Note: in some recent versions it's 'wavesurfer.js/plugins/regions'
// If Vite reports an error, switch the import below:
// import RegionsPlugin from 'wavesurfer.js/plugins/regions'
import RegionsPlugin from 'wavesurfer.js/plugins/regions'
import TimelinePlugin from 'wavesurfer.js/plugins/timeline'
// If your environment reports an error, temporarily fallback to:
// import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions'
import { Download, FileText, Play, Pause, Scissors, Timer, UploadCloud, Volume2, VolumeX, Trash2, RefreshCcw, UserPlus, Keyboard, MoveVertical, Tags, HelpCircle, X, AlertTriangle, Info, CheckCircle, XCircle, ArrowRight, ChevronsRight, ChevronsLeft, Filter } from 'lucide-react'
import { validateTranscript, autoFixTranscript, type QAResult } from './qa'
import JSZip from 'jszip'

const pad = (n: number, width = 2) => String(n).padStart(width, '0')
const toTimestamp = (seconds: number) => {
  if (!isFinite(seconds) || seconds < 0) seconds = 0
  const ms = Math.round((seconds % 1) * 1000)
  const s = Math.floor(seconds) % 60
  const m = Math.floor(seconds / 60) % 60
  const h = Math.floor(seconds / 3600)
  return `${pad(h)}:${pad(m)}:${pad(s)}.${pad(ms, 3)}`
}
const fromFileName = (name: string) => name.replace(/\.[^.]+$/, '')
// Helper: mm:ss or h:mm:ss for durations
const formatDurationShort = (sec: number) => {
  if (!isFinite(sec) || sec <= 0) return '0:00'
  const total = Math.floor(sec)
  const s = total % 60
  const m = Math.floor(total / 60) % 60
  const h = Math.floor(total / 3600)
  const pad2 = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad2(m)}:${pad2(s)}` : `${m}:${pad2(s)}`
}

// Helper: pretty print bytes
const formatBytes = (bytes: number) => {
  if (!isFinite(bytes) || bytes <= 0) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  let i = 0
  let v = bytes
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i++
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`
}

// Helper: get lowercase file extension
const getExt = (name: string) => {
  const m = name.match(/\.([^.]+)$/)
  return m ? m[1].toLowerCase() : ''
}

const BATCH_PREFIX_RE = /^[a-z][0-9]{6}_/i
const stripBatchPrefix = (value: string) => value.replace(BATCH_PREFIX_RE, '')
const normalizeBase = (name: string) => fromFileName(name).trim().toLowerCase()
const normalizeOriginalBase = (name: string) => stripBatchPrefix(fromFileName(name)).trim().toLowerCase()
const toJsonFileName = (filename: string) => {
  if (!filename) return 'file.json'
  const idx = filename.lastIndexOf('.')
  return idx >= 0 ? filename.slice(0, idx) + '.json' : filename + '.json'
}
const getOriginalFilename = (filename: string) => {
  if (!filename) return filename
  const stripped = filename.replace(BATCH_PREFIX_RE, '')
  return fromFileName(stripped || filename)
}

// Helper: choose tick interval (in seconds) based on pixels-per-second to keep notches ~≥25px apart
const pickIntervalPX = (px: number) => {
  const steps = [0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 15, 30, 60]
  for (const s of steps) {
    if (s * px >= 25) return s
  }
  return steps[steps.length - 1]
}

const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'm4a', 'flac', 'ogg'])
const isAudioFileName = (name: string) => AUDIO_EXTENSIONS.has(getExt(name))
const isJsonFileName = (name: string) => getExt(name) === 'json'
const sortItemsByName = (list: Item[]) =>
  [...list].sort((a, b) => a.filename.localeCompare(b.filename, undefined, { sensitivity: 'base' }))

const DEFAULT_TAGS = [
  // Con timestamp (range)
  { key: 'inaudible', needsRange: true, label: '[inaudible]' },
  { key: 'unintelligible', needsRange: true, label: '[unintelligible]' },
  { key: 'foreign', needsRange: true, label: '[foreign]' },
  { key: 'foreignLang', needsRange: true, label: '[foreign Language]' },
  { key: 'silence', needsRange: true, label: '[Silence]' },
  { key: 'crosstalk', needsRange: true, label: '[crosstalk]' },

  // Senza timestamp
  { key: 'laughter', needsRange: false, label: '[laughter]' },
  { key: 'clapping', needsRange: false, label: '[clapping]' },
  { key: 'music', needsRange: false, label: '[music]' },
  { key: 'noise', needsRange: false, label: '[noise]' },
  { key: 'singing', needsRange: false, label: '[singing]' },
  { key: 'attempt', needsRange: false, label: '[attempt]  [/attempt]' },
  { key: 'partialWord', needsRange: false, label: '[partial word]  [/partial word]' },
  { key: 'partialSentence', needsRange: false, label: '[partial sentence]' },

  // Puntuali
  { key: 'crosstalkPair', needsRange: false, label: '[crosstalk]  [/crosstalk]' },
] as const

const LANGS = ['English', 'Italian', 'Spanish', 'French', 'German', 'Portuguese', 'Unidentified']

const DIALECTS: Record<string, string[]> = {
  English: ['North America', 'United Kingdom & Ireland', 'Australasia', 'Latam', 'Africa', 'South Africa', 'West Asia & North Africa', 'South Asia', 'Southeast & East Asia', 'Central and Eastern Europe'],
  Italian: ['Standard', 'Northern', 'Central', 'Southern', 'Sardinian', 'Sicilian', 'Neapolitan'],
  Spanish: ['Spain (Castilian)', 'Mexico', 'Argentina', 'Colombia', 'Chile', 'Peru', 'Venezuela'],
  French: ['France', 'Canada (Quebec)', 'Belgium', 'Switzerland', 'Africa (var.)'],
  German: ['Germany', 'Austria', 'Switzerland'],
  Portuguese: ['Portugal (European)', 'Brazilian'],
  Unidentified: ['']
}

// File System Access API Types
interface FileSystemHandle {
  kind: 'file' | 'directory'
  name: string
  isSameEntry(other: FileSystemHandle): Promise<boolean>
}
interface FileSystemFileHandle extends FileSystemHandle {
  kind: 'file'
  getFile(): Promise<File>
  createWritable(options?: any): Promise<FileSystemWritableFileStream>
}
interface FileSystemDirectoryHandle extends FileSystemHandle {
  kind: 'directory'
  values(): AsyncIterableIterator<FileSystemHandle>
  getFileHandle(name: string, options?: { create?: boolean }): Promise<FileSystemFileHandle>
}
interface FileSystemWritableFileStream extends WritableStream {
  write(data: any): Promise<void>
  close(): Promise<void>
}

type Item = {
  id: string
  file?: File
  url: string
  filename: string
  basename: string
  audioHandle?: FileSystemFileHandle
  jsonHandle?: FileSystemFileHandle
  dirHandle?: FileSystemDirectoryHandle
  processed?: boolean
}

type Speaker = { name: string; gender?: string; dialect?: string }

type Meta = {
  language: string
  meeting: boolean
  speakers: Speaker[]
}

const DEFAULT_SPEAKER_COUNT = 4
const createDefaultSpeakers = () =>
  Array.from({ length: DEFAULT_SPEAKER_COUNT }, (_, idx) => ({
    name: `Speaker ${idx + 1}`,
    gender: '',
    dialect: '',
  }))
const cloneSpeakers = (list: Speaker[] = []) => list.map((s) => ({ ...s }))
const padSpeakers = (list: Speaker[] = []) => {
  const copy = cloneSpeakers(list)
  const missing = Math.max(0, DEFAULT_SPEAKER_COUNT - copy.length)
  for (let i = 0; i < missing; i++) {
    copy.push({ name: `Speaker ${copy.length + 1}`, gender: '', dialect: '' })
  }
  return copy
}

const escapeRegExp = (val: string) => val.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
const filterSpeakersInTranscript = (transcription: string, speakers: Speaker[] = []) => {
  if (!transcription || !speakers?.length) return []
  const used = new Set<string>()
  return speakers.reduce<Speaker[]>((acc, sp) => {
    const name = (sp?.name || '').trim()
    if (!name) return acc
    const key = name.toLowerCase()
    if (used.has(key)) return acc
    const re = new RegExp(`\\[\\s*${escapeRegExp(name)}\\s*\\]`, 'i')
    if (re.test(transcription)) {
      used.add(key)
      acc.push({ ...sp })
    }
    return acc
  }, [])
}

const createDefaultMeta = (): Meta => ({
  language: 'English',
  meeting: true,
  speakers: createDefaultSpeakers(),
})

type TranscriptImportPayload = {
  transcription: string
  language?: string
  meeting?: boolean
  speakers?: Speaker[]
}

type TranscriptEntry = { base: string; data: TranscriptImportPayload; rawName: string }

const collectDroppedFiles = async (dt: DataTransfer | null): Promise<{ audio: { file: File, handle?: FileSystemFileHandle, dirHandle?: FileSystemDirectoryHandle }[], json: { file: File, handle?: FileSystemFileHandle }[] }> => {
  if (!dt) return { audio: [], json: [] }

  const audioItems: { file: File, handle?: FileSystemFileHandle, dirHandle?: FileSystemDirectoryHandle }[] = []
  const jsonItems: { file: File, handle?: FileSystemFileHandle }[] = []

  const processEntry = async (entry: FileSystemHandle, parentDir?: FileSystemDirectoryHandle) => {
    if (entry.kind === 'file') {
      const fileHandle = entry as FileSystemFileHandle
      const file = await fileHandle.getFile()
      if (isAudioFileName(file.name)) {
        audioItems.push({ file, handle: fileHandle, dirHandle: parentDir })
      } else if (isJsonFileName(file.name)) {
        jsonItems.push({ file, handle: fileHandle })
      }
    } else if (entry.kind === 'directory') {
      const dirHandle = entry as FileSystemDirectoryHandle
      for await (const child of dirHandle.values()) {
        await processEntry(child, dirHandle)
      }
    }
  }

  const items = Array.from(dt.items)
  for (const item of items) {
    if (item.kind === 'file') {
      try {
        // @ts-ignore - getAsFileSystemHandle is modern API
        const handle = await item.getAsFileSystemHandle()
        if (handle) {
          await processEntry(handle)
          continue
        }
      } catch (e) {
        console.warn('FileSystemAccessAPI not supported or failed, falling back to File object', e)
      }

      // Fallback for browsers without FS Access API or if handle retrieval fails
      const file = item.getAsFile()
      if (file) {
        if (isAudioFileName(file.name)) {
          audioItems.push({ file })
        } else if (isJsonFileName(file.name)) {
          jsonItems.push({ file })
        }
      }
    }
  }

  return { audio: audioItems, json: jsonItems }
}

const extractTranscriptsFromValue = (value: any, fallbackBase: string): TranscriptEntry[] => {
  const entries: TranscriptEntry[] = []
  const visit = (candidate: any, idx: number) => {
    if (!candidate || typeof candidate !== 'object') return
    if (typeof candidate.transcription !== 'string') return
    const filenameValue =
      typeof candidate.filename === 'string' && candidate.filename.trim().length
        ? candidate.filename.trim()
        : `${fallbackBase}${idx ? `_${idx + 1}` : ''}`
    const normalizedName = filenameValue.split(/[\\/]/).pop() || filenameValue
    const baseRaw = fromFileName(normalizedName || fallbackBase || '')
    const base = baseRaw.trim().toLowerCase()
    if (!base) return
    const speakers = Array.isArray(candidate.speakers)
      ? candidate.speakers
        .map((s: any, sIdx: number) => {
          if (!s || typeof s !== 'object') return null
          const name =
            typeof s.name === 'string' && s.name.trim().length ? s.name.trim() : `Speaker ${sIdx + 1}`
          return {
            name,
            ...(typeof s.gender === 'string' ? { gender: s.gender } : {}),
            ...(typeof s.dialect === 'string' ? { dialect: s.dialect } : {}),
          }
        })
        .filter(Boolean) as Speaker[]
      : undefined
    const data: TranscriptImportPayload = {
      transcription: candidate.transcription,
      ...(speakers?.length ? { speakers } : {}),
    }
    if (typeof candidate.language === 'string' && candidate.language.trim().length) data.language = candidate.language
    if (typeof candidate.meeting === 'boolean') data.meeting = candidate.meeting
    entries.push({ base, data, rawName: normalizedName || fallbackBase || '' })
  }
  if (Array.isArray(value)) {
    value.forEach((v, idx) => visit(v, idx))
  } else {
    visit(value, 0)
  }
  return entries
}

const extractTranscriptsFromJsonFile = async (file: File) => {
  try {
    const text = await file.text()
    const payload = JSON.parse(text)
    const fallback = stripBatchPrefix(fromFileName(file.name)) || fromFileName(file.name)
    return extractTranscriptsFromValue(payload, fallback)
  } catch {
    return []
  }
}

const buildTranscriptMap = async (files: File[]) => {
  const map = new Map<string, TranscriptImportPayload>()
  for (const file of files) {
    const entries = await extractTranscriptsFromJsonFile(file)
    for (const entry of entries) {
      const keys = new Set<string>([
        entry.base,
        normalizeBase(entry.rawName || ''),
        normalizeOriginalBase(entry.rawName || ''),
        normalizeBase(file.name),
        normalizeOriginalBase(file.name),
      ])
      keys.forEach((key) => {
        if (key) map.set(key, entry.data)
      })
    }
  }
  return map
}

export default function App() {
  const [items, setItems] = useState<Item[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const [editing, setEditing] = useState<Record<string, string>>({})
  const [meta, setMeta] = useState<Record<string, Meta>>({})
  const [zoom, setZoom] = useState(0)
  const [volume, setVolume] = useState(1)
  const [playing, setPlaying] = useState(false)
  const [autoplayEnabled, setAutoplayEnabled] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const [selectedSpeakerIdx, setSelectedSpeakerIdx] = useState(0)
  const [qaOpen, setQaOpen] = useState(false)
  const [qa, setQa] = useState<QAResult | null>(null)
  const [hasRegion, setHasRegion] = useState(false)
  const [selectionRange, setSelectionRange] = useState<{ start: number; end: number } | null>(null)
  const [displayTime, setDisplayTime] = useState(0)
  const [activeDuration, setActiveDuration] = useState(0)
  const [durations, setDurations] = useState<Record<string, number>>({})
  const FAVORITE_TAG_KEYS = ['inaudible', 'unintelligible', 'silence', 'laughter', 'music', 'noise', 'attempt', 'foreignLang'] as const

  const wsRef = useRef<WaveSurfer | null>(null)
  const wsEl = useRef<HTMLDivElement | null>(null)
  const timelineEl = useRef<HTMLDivElement | null>(null)
  const regionsRef = useRef<ReturnType<typeof RegionsPlugin.create> | null>(null)
  const disableDragRef = useRef<null | (() => void)>(null)
  const pendingAutoplayRef = useRef(false)
  const zoomRAF = useRef<number | null>(null)
  const timelinePluginRef = useRef<ReturnType<typeof TimelinePlugin.create> | null>(null)
  const qaPanelRef = useRef<HTMLDivElement | null>(null)
  const scrollWrapRef = useRef<HTMLDivElement | null>(null)
  const [hasOverflowX, setHasOverflowX] = useState(false)
  const [scrollThumbLeft, setScrollThumbLeft] = useState(0)
  const [scrollThumbWidth, setScrollThumbWidth] = useState(100)
  const isReadyRef = useRef(false)
  const lastPxRef = useRef(0)
  const contentWRef = useRef(0)
  const [isMetaCollapsed, setIsMetaCollapsed] = useState(false)
  const [writeWarning, setWriteWarning] = useState<string | null>(null)
  const [meetingFalseOnly, setMeetingFalseOnly] = useState(false)


  const [isCompatible, setIsCompatible] = useState(true)

  useEffect(() => {
    if (!('showDirectoryPicker' in window)) {
      setIsCompatible(false)
    }
  }, [])

  if (!isCompatible) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-slate-200">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Browser Not Supported</h1>
          <p className="text-slate-600 mb-6">
            This application requires the <strong>File System Access API</strong> to read and write files directly to your computer.
          </p>
          <p className="text-slate-600 mb-6">
            Please use a Chromium-based browser such as <strong>Google Chrome</strong>, <strong>Microsoft Edge</strong>, <strong>Opera</strong>, or <strong>Brave</strong>.
          </p>
          <div className="text-xs text-slate-400">
            Safari and Firefox are currently not supported.
          </div>
        </div>
      </div>
    )
  }

  useEffect(() => {
    // Preload audio durations for the file list
    const toCleanup: HTMLAudioElement[] = []
    items.forEach((it) => {
      if (!it?.url) return
      if (durations[it.id] != null && isFinite(durations[it.id] as number)) return
      try {
        const audio = new Audio()
        audio.preload = 'metadata'
        audio.src = it.url
        const onMeta = () => {
          const d = isFinite(audio.duration) ? audio.duration : 0
          setDurations((prev) => (prev[it.id] === d ? prev : { ...prev, [it.id]: d }))
        }
        audio.addEventListener('loadedmetadata', onMeta)
        try { audio.load?.() } catch { }
        toCleanup.push(audio)
      } catch { }
    })
    return () => {
      toCleanup.forEach((a) => {
        try { a.src = '' } catch { }
      })
    }
  }, [items])

  const active = useMemo(() => items.find((i) => i.id === activeId) || null, [items, activeId])
  const hasWriteHandle = useCallback((it?: Item | null) => Boolean(it?.jsonHandle || it?.dirHandle), [])
  const activeHasWriteHandle = hasWriteHandle(active)
  const processedCount = useMemo(() => items.filter((it) => it.processed).length, [items])
  const remainingCount = Math.max(0, items.length - processedCount)
  const selectionDuration = selectionRange ? Math.max(0, selectionRange.end - selectionRange.start) : 0
  const visibleItems = useMemo(() => {
    if (!meetingFalseOnly) return items
    return items.filter((it) => meta[it.id]?.meeting === false)
  }, [items, meetingFalseOnly, meta])
  const activeVisible = useMemo(() => {
    if (!meetingFalseOnly || !activeId) return true
    return visibleItems.some((it) => it.id === activeId)
  }, [meetingFalseOnly, activeId, visibleItems])

  // Run QA before leaving the current file; block navigation unless the user explicitly confirms.
  const enforceQaBeforeLeaving = useCallback((action: 'switch' | 'next') => {
    if (!activeId) return true

    const key = String(activeId)
    const text = editing[key] || ''
    const baseMeta = meta[key]
    const m = baseMeta ? { ...baseMeta, speakers: padSpeakers(baseMeta.speakers) } : createDefaultMeta()
    const res = validateTranscript(text, m as any)

    // Always refresh QA results so the panel reflects the latest check
    setQa(res)
    if (res.counts.error > 0) {
      setQaOpen(true)
      requestAnimationFrame(() => {
        try {
          qaPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        } catch { }
      })
      const actionText = action === 'next' ? 'move to the next file' : 'switch files'
      const proceed = window.confirm(
        `QA detected ${res.counts.error} errors in the current transcription. You cannot continue unless you confirm.\nDo you still want to ${actionText}?`
      )
      if (!proceed) return false
    }

    return true
  }, [activeId, editing, meta])

  useEffect(() => {
    if (!items.length) {
      setWriteWarning(null)
      return
    }
    if (active && !hasWriteHandle(active)) {
      setWriteWarning('Cannot save the current file: no write permission. Re-import the folder granting access or use Export All (ZIP).')
      return
    }
    const missingSome = items.some((it) => !hasWriteHandle(it))
    if (missingSome) {
      setWriteWarning('Some files lack write permission: saving may fail. Re-import the folder granting access or use Export All (ZIP).')
    } else {
      setWriteWarning(null)
    }
  }, [items, active, hasWriteHandle])

  useEffect(() => {
    if (!wsEl.current) return
    if (wsRef.current) {
      wsRef.current.destroy()
      wsRef.current = null
    }
    if (!active) {
      setPlaying(false)
      setSelectionRange(null)
      pendingAutoplayRef.current = false
      return
    }

    // reset ready flag for new instance
    isReadyRef.current = false
    setHasRegion(false)
    setPlaying(false)
    setSelectionRange(null)
    pendingAutoplayRef.current = autoplayEnabled
    // close QA panel when switching file (its content is per-file)
    setQaOpen(false)
    setQa(null)

    const ws = WaveSurfer.create({
      container: wsEl.current,
      url: active.url,
      waveColor: '#cbd5e1',
      progressColor: '#0ea5e9',
      cursorColor: '#0ea5e9',
      height: 72,
      normalize: true,
      fillParent: false, // prevent stretch to container so zoom uses exact px/sec and preserves shape at right edge
    }) as any

    // Explicitly register the Regions plugin and keep a reference
    const regions = RegionsPlugin.create()
    ws.registerPlugin(regions)
    regionsRef.current = regions

    // Apply initial zoom after audio is ready: baseline fits container width, then scale by slider
    const applyZoomFromSlider = () => {
      const wrap = scrollWrapRef.current
      if (!wrap) return
      const dur = (ws as any).getDuration?.() || 0
      const w = wrap.clientWidth || 0
      if (dur <= 0 || w <= 0) return
      const fit = w / dur
      const factor = Math.max(1, 1 + (zoom / 50)) // 0..200 => 1x..5x
      const px = fit * factor
      try { (ws as any).zoom?.(px) } catch { }
      // update overflow + mini-track using explicit width tied to px/sec
      try {
        lastPxRef.current = px
        const dur2 = (ws as any).getDuration?.() || 0
        const total = Math.max(w, Math.floor(dur2 * px))
        if (wsEl.current) (wsEl.current as HTMLDivElement).style.width = `${total}px`
        if (timelineEl.current) (timelineEl.current as HTMLDivElement).style.width = `${total}px`
        const cw = wrap.clientWidth || 1
        const sl = wrap.scrollLeft || 0
        setHasOverflowX(total > cw)
        const widthPct = Math.max(5, Math.min(100, (cw / total) * 100))
        const leftPct = Math.max(0, Math.min(100 - widthPct, (sl / total) * 100))
        setScrollThumbWidth(widthPct)
        setScrollThumbLeft(leftPct)
      } catch { }
    }
    ws.on('ready', () => {
      isReadyRef.current = true
      try {
        setActiveDuration(((ws as any).getDuration?.() || 0))
        setDisplayTime(((ws as any).getCurrentTime?.() || 0))
        if (pendingAutoplayRef.current && autoplayEnabled) {
          try { (ws as any).play?.() } catch { }
        }
      } catch { }
      pendingAutoplayRef.current = false
      requestAnimationFrame(applyZoomFromSlider)
    })

    // Sync displayed time on playback progress and seeks/clicks
    try {
      ws.on('timeupdate', (t: number) => setDisplayTime(typeof t === 'number' ? t : ((ws as any).getCurrentTime?.() || 0)))
      ws.on('seeking', (t: number) => setDisplayTime(typeof t === 'number' ? t : ((ws as any).getCurrentTime?.() || 0)))
      ws.on('seek', (progress: number) => {
        const dur = (ws as any).getDuration?.() || 0
        setDisplayTime(Math.max(0, Math.min(dur, (typeof progress === 'number' ? progress : 0) * dur)))
      })
    } catch { }

    const timeline = TimelinePlugin.create({
      container: timelineEl.current!,
      height: 24,
      // Keep labels stable across zoom levels (avoid "missing" ticks)
      primaryLabelInterval: (pxPerSec: number) => pickIntervalPX(pxPerSec),
      secondaryLabelInterval: (pxPerSec: number) => Math.max(pickIntervalPX(pxPerSec) / 4, 0.01),
      style: { fontSize: '10px', color: '#475569' },
    } as any)
    timelinePluginRef.current = timeline
    ws.registerPlugin(timeline)

    // Enable region creation via mouse drag with a handle to disable/re-enable it
    // drag handle stored in disableDragRef
    const reenableDragSelection = () => {
      try { disableDragRef.current?.() } catch { }
      try { disableDragRef.current = (regions as any).enableDragSelection({ color: 'rgba(14,165,233,0.25)' }) as any } catch { }
    }
    reenableDragSelection()

    // UX: click on a region => jump to its start, double-click => play range
    regions.on('region-clicked', (region: any, e: MouseEvent) => {
      e.stopPropagation()
      ws.setTime(region.start)
      setSelectionRange({ start: region.start, end: region.end })
      if ((e.detail ?? 1) >= 2) {
        ws.play(region.start, region.end)
      }
    })

    // UX: when created, make it draggable/resizable and enforce a single region
    regions.on('region-created', (region: any) => {
      region.update({ drag: true, resize: true, color: 'rgba(14,165,233,0.25)' })

      // Keep only one region: remove any other existing ones
      const all = ((regions as any).getRegions?.() ?? Object.values((regions as any).regions || {})) as any[]
      all.forEach((r: any) => { if (r.id !== region.id) r.remove() })

      // Guard against click-only zero-length regions
      if ((region.end - region.start) < 0.05) {
        region.remove()
        // Click-only selection: allow user to try again by re-enabling drag selection
        reenableDragSelection()
        setHasRegion(false)
        setSelectionRange(null)
        return
      }

      setHasRegion(true)
      setSelectionRange({ start: region.start, end: region.end })

      // Stop drag selection immediately after a region is created,
      // so the selection detaches from the mouse cursor.
      try { disableDragRef.current?.(); disableDragRef.current = null } catch { }
    })

    regions.on('region-updated', (region: any) => {
      setHasRegion(true)
      setSelectionRange({ start: region.start, end: region.end })
    })


    // Global fallback: finalize selection on any pointerup; if no region exists, re-enable for a new selection
    const onGlobalPointerUp = () => {
      try { disableDragRef.current?.(); disableDragRef.current = null } catch { }
      const list = ((regions as any).getRegions?.() ?? Object.values((regions as any).regions || {})) as any[]
      const anyExists = list.length > 0
      setHasRegion(anyExists)
      if (!anyExists) {
        reenableDragSelection()
        setSelectionRange(null)
      } else {
        const last = list[list.length - 1]
        if (last) setSelectionRange({ start: last.start, end: last.end })
      }
    }
    window.addEventListener('pointerup', onGlobalPointerUp)
    window.addEventListener('mouseup', onGlobalPointerUp)
    window.addEventListener('blur', onGlobalPointerUp)

    wsRef.current = ws

    ws.on('play', () => setPlaying(true))
    ws.on('pause', () => setPlaying(false))

    return () => {
      window.removeEventListener('pointerup', onGlobalPointerUp)
      window.removeEventListener('mouseup', onGlobalPointerUp)
      window.removeEventListener('blur', onGlobalPointerUp)
      regions.destroy?.()
      ws.destroy()
    }
  }, [activeId])

  const saveTranscript = async (itemId: string) => {
      const item = items.find(i => i.id === itemId)
      if (!item) return

      const transcription = editing[itemId] || ''
      const baseMeta = meta[itemId]
      const m = baseMeta ? { ...baseMeta, speakers: padSpeakers(baseMeta.speakers) } : createDefaultMeta()
      const speakersForJson = filterSpeakersInTranscript(transcription, m.speakers)
      const originalFilename = getOriginalFilename(item.filename)

      const payload = {
        filename: originalFilename,
        transcription,
        ...(speakersForJson.length ? { speakers: speakersForJson } : {}),
        ...(m.language ? { language: m.language } : {}),
        ...(typeof m.meeting === 'boolean' ? { meeting: m.meeting } : {}),
      }

      const content = JSON.stringify(payload, null, 4)

      try {
        let handle = item.jsonHandle
        if (!handle && item.dirHandle) {
          const jsonName = toJsonFileName(item.filename)
          handle = await item.dirHandle.getFileHandle(jsonName, { create: true })
          // Update item with new handle so future saves are faster
          setItems(prev => prev.map(i => i.id === itemId ? { ...i, jsonHandle: handle } : i))
        }

        if (!handle) {
          const message = 'Cannot save: no JSON handle and no parent directory handle available. Please re-import the folder with write permission or use Export All.'
          setWriteWarning(message)
          throw new Error(message)
        }

        const writable = await handle.createWritable()
        await writable.write(content)
        await writable.close()
      } catch (e: any) {
        console.error('Failed to save to disk', e)
        const msg = e?.message || 'Error saving file to disk. Check console for details.'
        alert(msg)
        throw e
      }
    }

  // Auto-save when switching active file
  const switchFile = async (newId: string | null) => {
    if (newId === activeId) return

    if (activeId) {
      try {
        await saveTranscript(activeId)
      } catch {
        // Stop switching if we cannot save the current file
        return
      }
      const canLeave = enforceQaBeforeLeaving('switch')
      if (!canLeave) return
    }
    setActiveId(newId)
  }

  const handleNextFile = async () => {
    if (!activeId || !active) return

    // 1. Save current (stop if it fails)
    try {
      await saveTranscript(activeId)
    } catch {
      return
    }

    // 2. Run QA and block unless confirmed
    const canLeave = enforceQaBeforeLeaving('next')
    if (!canLeave) return

    // 3. Mark current as processed and move to next file (keep list intact)
    const currentIndex = items.findIndex(i => i.id === activeId)
    if (currentIndex === -1) return

    const nextActiveId =
      currentIndex + 1 < items.length
        ? items[currentIndex + 1].id // next item in list
        : items[currentIndex - 1]?.id || items[currentIndex]?.id // if last, stay on previous or current

    setItems(prev => prev.map(it => it.id === activeId ? { ...it, processed: true } : it))
    setActiveId(nextActiveId ?? activeId)

    // If we switched to a new file, we might want to auto-play or just be ready.
    // The useEffect for activeId will handle loading the new waveform.
  }

  const onDrop = useCallback(
    async (ev: React.DragEvent<HTMLDivElement> | any) => {
      ev.preventDefault?.()

      const { audio: audioItems, json: jsonItems } = await collectDroppedFiles(ev.dataTransfer || null)
      if (!audioItems.length && !jsonItems.length) return

      const replacingExisting = audioItems.length > 0 && items.length > 0
      if (replacingExisting) {
        const proceed = window.confirm(
          `Importing new audio will remove ${items.length} file${items.length === 1 ? '' : 's'} currently loaded. Continue?`
        )
        if (!proceed) return
      }

      const jsonFiles = jsonItems.map(i => i.file)
      const transcriptMap = jsonFiles.length
        ? await buildTranscriptMap(jsonFiles)
        : new Map<string, TranscriptImportPayload>()

      // Map base names to JSON handles for easy lookup
      const jsonHandleMap = new Map<string, FileSystemFileHandle>()
      jsonItems.forEach(item => {
        if (item.handle) {
          jsonHandleMap.set(normalizeBase(item.file.name), item.handle)
          jsonHandleMap.set(normalizeOriginalBase(item.file.name), item.handle)
        }
      })

      const newItems: Item[] = audioItems.map((item) => {
        const nBase = normalizeBase(item.file.name)
        const nOrig = normalizeOriginalBase(item.file.name)
        const jsonHandle = jsonHandleMap.get(nBase) || jsonHandleMap.get(nOrig)

        return {
          id: crypto.randomUUID(),
          file: item.file,
          url: URL.createObjectURL(item.file),
          filename: item.file.name,
          basename: fromFileName(item.file.name),
          audioHandle: item.handle,
          dirHandle: item.dirHandle,
          jsonHandle,
          processed: false,
        }
      })

      const nextEditing = replacingExisting ? {} : { ...editing }
      const nextMeta = replacingExisting ? {} : { ...meta }
      let editingChanged = false
      let metaChanged = false

      newItems.forEach((it) => {
        if (!(it.id in nextEditing)) {
          nextEditing[it.id] = ''
          editingChanged = true
        }
        if (!nextMeta[it.id]) {
          nextMeta[it.id] = createDefaultMeta()
          metaChanged = true
        }
      })

      if (transcriptMap.size) {
        const applyTranscript = (it: Item) => {
          const keyCandidates = new Set<string>([
            it.basename.trim().toLowerCase(),
            normalizeBase(it.filename),
            normalizeOriginalBase(it.filename),
            normalizeOriginalBase(it.basename),
          ])
          let data: TranscriptImportPayload | undefined
          for (const key of keyCandidates) {
            if (!key) continue
            const found = transcriptMap.get(key)
            if (found) {
              data = found
              break
            }
          }
          if (!data) return
          if (nextEditing[it.id] !== data.transcription) {
            nextEditing[it.id] = data.transcription
            editingChanged = true
          }
          const prevMeta = nextMeta[it.id]
            ? { ...nextMeta[it.id], speakers: padSpeakers(nextMeta[it.id].speakers) }
            : createDefaultMeta()
          const updatedMeta: Meta = {
            ...prevMeta,
            ...(data.language ? { language: data.language } : {}),
            ...(typeof data.meeting === 'boolean' ? { meeting: data.meeting } : {}),
            speakers: padSpeakers(data.speakers?.length ? data.speakers : prevMeta.speakers),
          }
          nextMeta[it.id] = updatedMeta
          metaChanged = true
        }
        const targets = replacingExisting ? newItems : [...items, ...newItems]
        targets.forEach(applyTranscript)
      }

      if (newItems.length) {
        const sortedNewItems = sortItemsByName(newItems)
        if (replacingExisting) {
          setItems(sortedNewItems)
          setActiveId(sortedNewItems[0]?.id ?? null)
        } else {
          setItems((prev) => sortItemsByName([...prev, ...newItems]))
          setActiveId((prev) => prev ?? sortedNewItems[0].id)
        }
      } else if (replacingExisting) {
        setItems([])
        setActiveId(null)
      }

      if (editingChanged || replacingExisting) setEditing(nextEditing)
      if (metaChanged || replacingExisting) setMeta(nextMeta)
    },
    [editing, items, meta]
  )

  const onFileInput = useCallback((ev: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(ev.target.files || [])
    if (!files.length) return
    const dt = new DataTransfer()
    files.forEach((f) => dt.items.add(f))
    const fakeEv = { preventDefault() { }, dataTransfer: dt } as any
    void onDrop(fakeEv)
  }, [onDrop])

  const currentTime = () => (wsRef.current ? (wsRef.current as any).getCurrentTime() : 0)

  const insertAtCursor = (text: string) => {
    const ta = document.getElementById('transcript-area') as HTMLTextAreaElement | null
    if (!ta) return
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const before = ta.value.slice(0, start)
    const after = ta.value.slice(end)
    const next = before + text + after
    ta.value = next
    setEditing({ ...editing, [activeId as string]: next })
    const pos = start + text.length
    setTimeout(() => {
      ta.focus()
      ta.selectionStart = ta.selectionEnd = pos
    }, 0)
  }

  // Insert tag while being aware of current selection:
  // - If selection exists and is a tag, OVERWRITE it with the new tag (single or pair).
  // - If a selection exists and 'single' is provided, prefix the selection with that single tag,
  //   ensuring exactly one space between the preceding word and the tag and one space between tag and selection.
  // - If a selection exists and 'open'+'close' are provided, wrap the selection with those,
  //   ensuring a space after open and before close, and a space before the open if it's attached to a word.
  // - If no selection, insert at cursor and, for single tags, ensure a space before (if attached to a word)
  //   and append a trailing space if needed so the next typed text is separated.
  const insertTagWithSelection = (spec: { single?: string; open?: string; close?: string; fallback?: string }) => {
    const ta = document.getElementById('transcript-area') as HTMLTextAreaElement | null
    if (!ta) return
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const hasSelection = start !== end

    // Helpers
    const isTagLike = (s: string) => {
      const t = (s || '').trim()
      if (!t) return false
      // Single bracketed tag e.g. [inaudible], [Silence 00:00:01.000-00:00:02.000], [/crosstalk 00:...]
      if (/^\[[^\]]+\]$/.test(t)) return true
      // Two bracketed tags side-by-side e.g. [attempt]  [/attempt]
      if (/^\[[^\]]+\]\s*\[\/?[^\]]+\]$/.test(t)) return true
      return false
    }
    const needsLeadingSpace = (before: string) => before.length > 0 && !/\s$/.test(before)
    const needsTrailingSpace = (after: string) => after.length > 0 && !/^\s/.test(after)

    if (hasSelection) {
      const val = ta.value
      const before = val.slice(0, start)
      const selected = val.slice(start, end)
      const after = val.slice(end)

      // If the user selected an existing tag, overwrite it with the new tag instead of prefixing/wrapping
      if (isTagLike(selected)) {
        let ins = ''
        if (spec.open !== undefined && spec.close !== undefined) {
          const openStr = spec.open ?? ''
          const closeStr = spec.close ?? ''
          ins = openStr + closeStr
        } else if (spec.single !== undefined) {
          ins = spec.single || ''
        } else {
          ins = spec.fallback || ''
        }

        // Ensure spacing between preceding word and the tag, and between the tag and the following word
        if (needsLeadingSpace(before) && !ins.startsWith(' ')) ins = ' ' + ins
        if (needsTrailingSpace(after) && !ins.endsWith(' ')) ins = ins + ' '

        const next = before + ins + after
        ta.value = next
        setEditing({ ...editing, [activeId as string]: next })
        const caret = before.length + ins.length
        setTimeout(() => {
          ta.focus()
          ta.selectionStart = ta.selectionEnd = caret
        }, 0)
        return
      }

      // Normal case: selection is not a tag
      let next = ''
      let selStart = start
      let selEnd = end

      if (spec.open !== undefined && spec.close !== undefined) {
        // Ensure space after open and before close; and a leading space if attached to the previous word
        const openStr = spec.open ?? ''
        const closeStr = spec.close ?? ''
        const openWithSpace = openStr.endsWith(' ') ? openStr : openStr + ' '
        const closeWithSpace = closeStr.startsWith(' ') ? closeStr : ' ' + closeStr
        const lead = needsLeadingSpace(before) ? ' ' : ''
        next = before + lead + openWithSpace + selected + closeWithSpace + after
        selStart = start + lead.length + openWithSpace.length
        selEnd = selStart + selected.length
      } else if (spec.single !== undefined) {
        // Ensure a single space between tag and selected text; and a leading space if attached to the previous word
        const single = spec.single || ''
        const needsBetween = !single.endsWith(' ') && !selected.startsWith(' ')
        const singleWithSpace = needsBetween ? (single + ' ') : single
        const lead = needsLeadingSpace(before) ? ' ' : ''
        next = before + lead + singleWithSpace + selected + after
        selStart = start + lead.length + singleWithSpace.length
        selEnd = selStart + selected.length
      } else {
        // Fallback insertion respecting leading boundary spacing
        const ins = spec.fallback || ''
        const lead = needsLeadingSpace(before) ? ' ' : ''
        next = before + lead + ins + after
        selStart = selEnd = start + lead.length + ins.length
      }

      ta.value = next
      setEditing({ ...editing, [activeId as string]: next })
      setTimeout(() => {
        ta.focus()
        ta.selectionStart = selStart
        ta.selectionEnd = selEnd
      }, 0)
    } else {
      // Insert at cursor. For single-like insertions, ensure:
      // - a leading space if attached to a word
      // - a trailing space so the next typed text is separated
      let ins = spec.fallback ?? spec.single ?? ((spec.open ?? '') + (spec.close ?? ''))
      const isSingleLike = spec.open === undefined && spec.close === undefined
      const charAfter = ta.value.slice(start, start + 1)
      const charBefore = ta.value.slice(Math.max(0, start - 1), start)
      if (isSingleLike) {
        if (!/\s$/.test(ins) && !/^\s/.test(charAfter || '')) ins += ' '
        if (!/^\s/.test(ins) && !/\s$/.test(charBefore || '')) ins = ' ' + ins
      }
      insertAtCursor(ins)
    }
  }

  const addTag = (tag: typeof DEFAULT_TAGS[number]) => {
    if (!active) return
    const ws: any = wsRef.current
    const regions = regionsRef.current as any

    // Paired tags: wrap selection, otherwise insert open+close and place cursor at default position
    if (tag.key === 'attempt') {
      const open = '[attempt] '
      const close = ' [/attempt]'
      insertTagWithSelection({ open, close, fallback: `${open}${close}` })
      return
    }
    if (tag.key === 'partialWord') {
      const open = '[partial word] '
      const close = ' [/partial word]'
      insertTagWithSelection({ open, close, fallback: `${open}${close}` })
      return
    }

    // Point-in-time crosstalk: insert paired tags with selection timestamps (or cursor)
    if (tag.key === 'crosstalkPair') {
      let start = ws.getCurrentTime()
      let end = start
      if (selectionRange) {
        start = selectionRange.start
        end = selectionRange.end
      } else if (regions) {
        const list = (((regions as any).getRegions?.() ?? Object.values((regions as any).regions || {})) as any[])
        const r = list[list.length - 1]
        if (r) {
          start = r.start
          end = r.end
        }
      }
      const open = `[crosstalk ${toTimestamp(start)}] `
      const close = ` [/crosstalk ${toTimestamp(end)}]`
      insertTagWithSelection({ open, close, fallback: `${open}${close}` })
      return
    }

    // Range-based tags (with start-end timestamps): prefix selection if any
    if (tag.needsRange) {
      const list = regions ? (((regions as any).getRegions?.() ?? Object.values((regions as any).regions || {})) as any[]) : []
      const r = list[list.length - 1]
      const start = r ? r.start : ws.getCurrentTime()
      const end = r ? r.end : start

      let toInsert: string
      if (tag.key === 'foreignLang') {
        const lang = (window.prompt('Language to annotate in [foreign <Language> ...]', '') || 'Language').trim()
        toInsert = `[foreign ${lang || 'Language'} ${toTimestamp(start)}-${toTimestamp(end)}]`
      } else {
        toInsert = `${tag.label.replace(/\]$/, '')} ${toTimestamp(start)}-${toTimestamp(end)}]`
      }
      insertTagWithSelection({ single: toInsert, fallback: toInsert })
      return
    }

    // Simple tags without timestamps: prefix selection if any
    insertTagWithSelection({ single: tag.label, fallback: tag.label })
  }

  const addSpeaker = () => {
    if (!active) return
    const m = meta[activeId as string]
    const n = m.speakers.length + 1
    const updated = { ...meta, [activeId as string]: { ...m, speakers: [...m.speakers, { name: `Speaker ${n}`, gender: '', dialect: '' }] } }
    setMeta(updated)
  }

  const insertSpeakerTag = (idx: number) => {
    const m = meta[activeId as string]
    if (!m?.speakers?.[idx]) return
    insertAtCursor(`[${m.speakers[idx].name}] `)
  }


  const togglePlay = () => {
    if (!wsRef.current) return
      ; (wsRef.current as any).playPause()
  }

  // Smooth zoom: throttle and keep a minimum "fit to width" baseline
  useEffect(() => {
    const wsAny: any = wsRef.current
    const wrap = scrollWrapRef.current
    if (!wsAny || !wrap || !isReadyRef.current) return
    if (zoomRAF.current) cancelAnimationFrame(zoomRAF.current)
    zoomRAF.current = requestAnimationFrame(() => {
      const dur = wsAny.getDuration?.() || 0
      const w = wrap.clientWidth || 0
      if (dur > 0 && w > 0) {
        const fit = w / dur
        const factor = Math.max(1, 1 + (zoom / 50)) // 1x..5x
        const px = fit * factor
        try { wsAny.zoom?.(px) } catch { }
        try {
          lastPxRef.current = px
          const dur2 = wsAny.getDuration?.() || 0
          const total = Math.max(w, Math.floor(dur2 * px))
          if (wsEl.current) (wsEl.current as HTMLDivElement).style.width = `${total}px`
          if (timelineEl.current) (timelineEl.current as HTMLDivElement).style.width = `${total}px`
          const cw = wrap.clientWidth || 1
          const sl = wrap.scrollLeft || 0
          setHasOverflowX(total > cw)
          const widthPct = Math.max(5, Math.min(100, (cw / total) * 100))
          const leftPct = Math.max(0, Math.min(100 - widthPct, (sl / total) * 100))
          setScrollThumbWidth(widthPct)
          setScrollThumbLeft(leftPct)
        } catch { }
      }
      zoomRAF.current = null
    })
    return () => {
      if (zoomRAF.current) {
        cancelAnimationFrame(zoomRAF.current)
        zoomRAF.current = null
      }
    }
  }, [zoom])

  // Keep overflow indicator accurate on container resize and maintain fit baseline
  useEffect(() => {
    const wrap = scrollWrapRef.current
    if (!wrap) return
    const ro = new ResizeObserver(() => {
      try {
        setHasOverflowX(wrap.scrollWidth > wrap.clientWidth)
        const sw = wrap.scrollWidth || 1
        const cw = wrap.clientWidth || 1
        const sl = wrap.scrollLeft || 0
        const widthPct = Math.max(5, Math.min(100, (cw / sw) * 100))
        const leftPct = Math.max(0, Math.min(100 - widthPct, (sl / sw) * 100))
        setScrollThumbWidth(widthPct)
        setScrollThumbLeft(leftPct)
      } catch { }

      // When size changes, recompute px/sec from fit baseline (if audio is ready)
      const wsAny: any = wsRef.current
      if (!wsAny || !isReadyRef.current) return
      const dur = wsAny.getDuration?.() || 0
      const w = wrap.clientWidth || 0
      if (dur > 0 && w > 0) {
        const fit = w / dur
        const factor = Math.max(1, 1 + (zoom / 50)) // 1x..5x
        const px = fit * factor
        try { wsAny.zoom?.(px) } catch { }
        try {
          lastPxRef.current = px
          const dur2 = wsAny.getDuration?.() || 0
          const total = Math.max(w, Math.floor(dur2 * px))
          if (wsEl.current) (wsEl.current as HTMLDivElement).style.width = `${total}px`
          if (timelineEl.current) (timelineEl.current as HTMLDivElement).style.width = `${total}px`
          const cw = wrap.clientWidth || 1
          const sl = wrap.scrollLeft || 0
          setHasOverflowX(total > cw)
          const widthPct = Math.max(5, Math.min(100, (cw / total) * 100))
          const leftPct = Math.max(0, Math.min(100 - widthPct, (sl / total) * 100))
          setScrollThumbWidth(widthPct)
          setScrollThumbLeft(leftPct)
        } catch { }
      }
    })
    ro.observe(wrap)
    return () => ro.disconnect()
  }, [zoom])

  const addRegionFromSelection = () => {
    const ws: any = wsRef.current
    const regions = regionsRef.current as any
    if (!ws || !regions) return
    // Keep only one: remove existing region if present, then create a 2s region centered on playhead
    (((regions as any).getRegions?.() ?? Object.values((regions as any).regions || {})) as any[]).forEach((r: any) => r.remove())
    const center = ws.getCurrentTime()
    const start = Math.max(0, center - 1)
    const end = center + 1
    regions.addRegion({ start, end, drag: true, resize: true, color: 'rgba(14,165,233,0.25)' })
    setHasRegion(true)
    setSelectionRange({ start, end })
  }

  const clearRegions = () => {
    const regions = regionsRef.current as any
    if (!regions) return
    (((regions as any).getRegions?.() ?? Object.values((regions as any).regions || {})) as any[]).forEach((r: any) => r.remove())
    try { disableDragRef.current?.(); disableDragRef.current = (regions as any).enableDragSelection?.({ color: 'rgba(14,165,233,0.25)' }) } catch { }
    setHasRegion(false)
    setSelectionRange(null)
  }


  const exportJSON = () => {
    if (!active) return
    const key = String(activeId)
    const transcription = (editing[key] || '')
    const baseMeta = meta[key]
    const m = baseMeta ? { ...baseMeta, speakers: padSpeakers(baseMeta.speakers) } : createDefaultMeta()
    const originalFilename = getOriginalFilename(active.filename)
    const jsonFileName = toJsonFileName(active.filename)

    // Run QA on-the-fly to gate export, but allow override if needed
    const res = validateTranscript(transcription, m as any)
    // Update visible QA so the user can inspect if they wish
    setQa(res)

    if (res.counts.error > 0) {
      setQaOpen(true)
      const proceed = window.confirm(`QA detected ${res.counts.error} errors and ${res.counts.warning} warnings. Do you want to force the export anyway?`)
      if (!proceed) return
    } else if (res.counts.warning > 0) {
      const proceed = window.confirm(`QA detected ${res.counts.warning} warnings. Proceed with export?`)
      if (!proceed) return
    }

    const speakersForJson = filterSpeakersInTranscript(transcription, m.speakers)
    const payload = [
      {
        filename: originalFilename,
        transcription,
        ...(speakersForJson.length
          ? { speakers: speakersForJson.map((s) => ({ name: s.name, ...(s.gender ? { gender: s.gender } : {}), ...(s.dialect ? { dialect: s.dialect } : {}), })) }
          : {}),
        ...(m.language ? { language: m.language } : {}),
        ...(typeof m.meeting === 'boolean' ? { meeting: m.meeting } : {}),
      },
    ]

    const blob = new Blob([JSON.stringify(payload, null, 4)], { type: 'application/json;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = jsonFileName
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  const exportAllZip = async () => {
    if (!items.length) return

    const slugify = (s: string) =>
      (s || '')
        .replace(/[^a-z0-9_-]+/gi, '_')
        .replace(/^_+|_+$/g, '')
        .toLowerCase()

    const now = new Date()
    const pad2 = (n: number) => String(n).padStart(2, '0')
    const zipName = `LE_Export_${now.getFullYear()}${pad2(now.getMonth() + 1)}${pad2(now.getDate())}_${pad2(now.getHours())}${pad2(now.getMinutes())}.zip`

    // Build per-file payloads and QA summaries first (so we can confirm on errors)
    const perFile = items.map((it) => {
      const key = it.id
      const transcription = (editing[key] || '')
      const baseMeta = meta[key]
      const m = baseMeta ? { ...baseMeta, speakers: padSpeakers(baseMeta.speakers) } : createDefaultMeta()
      const res = validateTranscript(transcription, m)
      const originalFilename = getOriginalFilename(it.filename)
      const jsonFileName = toJsonFileName(it.filename)
      const speakersForJson = filterSpeakersInTranscript(transcription, m.speakers)
      const payload = [
        {
          filename: originalFilename,
          transcription,
          ...(speakersForJson.length
            ? {
              speakers: speakersForJson.map((s) => ({
                name: s.name,
                ...(s.gender ? { gender: s.gender } : {}),
                ...(s.dialect ? { dialect: s.dialect } : {}),
              })),
            }
            : {}),
          ...(m.language ? { language: m.language } : {}),
          ...(typeof m.meeting === 'boolean' ? { meeting: m.meeting } : {}),
        },
      ]
      return {
        it,
        key,
        m,
        transcription,
        res,
        payload,
        safeBase: slugify(it.basename),
        jsonFileName,
      }
    })

    const totalErrors = perFile.reduce((a, r) => a + r.res.counts.error, 0)
    const totalWarnings = perFile.reduce((a, r) => a + r.res.counts.warning, 0)

    if (totalErrors > 0) {
      const proceed = window.confirm(
        `QA detected issues in ${perFile.filter((r) => r.res.counts.error > 0).length} files (total errors: ${totalErrors}, warnings: ${totalWarnings}).\nDo you want to force exporting ALL files anyway?`
      )
      if (!proceed) return
    } else if (totalWarnings > 0) {
      const proceed = window.confirm(
        `QA detected warnings only (total warnings: ${totalWarnings}). Proceed with exporting all files?`
      )
      if (!proceed) return
    }

    const zip = new JSZip()

    // Flat structure: only transcription JSON files at root
    for (const row of perFile) {
      zip.file(row.jsonFileName, JSON.stringify(row.payload, null, 4))
    }

    const blob = await zip.generateAsync({ type: 'blob' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = zipName
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  const runQA = useCallback(() => {
    const key = String(activeId)
    const text = editing[key] || ''
    const baseMeta = meta[key]
    const m = baseMeta ? { ...baseMeta, speakers: padSpeakers(baseMeta.speakers) } : createDefaultMeta()
    const res = validateTranscript(text, m as any)
    setQa(res)
    setQaOpen(true)
    // Ensure the QA panel becomes visible to the user
    requestAnimationFrame(() => {
      try {
        qaPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      } catch { }
    })
  }, [activeId, editing, meta])

  const applyAutoFixes = useCallback(() => {
    const key = String(activeId)
    const text = editing[key] || ''
    const fixed = autoFixTranscript(text)
    if (fixed.changed) {
      const next = { ...editing, [key]: fixed.text }
      setEditing(next)
      const baseMeta = meta[key]
      const m = baseMeta ? { ...baseMeta, speakers: padSpeakers(baseMeta.speakers) } : createDefaultMeta()
      setQa(validateTranscript(fixed.text, m as any))
    }
  }, [activeId, editing, meta])

  const jumpToIssue = useCallback((iss: { index: number; length: number }) => {
    const ta = document.getElementById('transcript-area') as HTMLTextAreaElement | null
    if (!ta) return
    const start = Math.max(0, Math.min(iss.index || 0, ta.value.length))
    const end = Math.max(start, Math.min((iss.index || 0) + (iss.length || 0), ta.value.length))
    ta.focus()
    try {
      ta.setSelectionRange(start, end)
    } catch { }
  }, [])

  const renderHighlightedTranscript = (text: string) => {
    if (!text) return <span className="text-slate-400">Write the transcription here...</span>
    return text.split(/(\[[^\]]+\])/g).map((part, idx) => {
      if (!part) return null
      const isTag = /^\[[^\]]+\]$/.test(part.trim())
      return isTag
        ? <span key={idx} className="bg-amber-100 text-amber-900 font-semibold">{part}</span>
        : <span key={idx}>{part}</span>
    })
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!active) return
      const k = e.key.toLowerCase()
      if (e.ctrlKey && k === ' ') { e.preventDefault(); togglePlay() }
      if (e.altKey && k === 'i') { e.preventDefault(); addTag(DEFAULT_TAGS[0]) }
      if (e.altKey && k === 's') { e.preventDefault(); addTag(DEFAULT_TAGS.find(t => t.key === 'silence') as typeof DEFAULT_TAGS[number]) }
      if (e.altKey && k === 'r') { e.preventDefault(); addRegionFromSelection() }
      if (e.altKey && (k === 'arrowleft' || k === 'left' || k === '-')) { e.preventDefault(); wsRef.current && (wsRef.current as any).skip?.(-5) }
      if (e.altKey && (k === 'arrowright' || k === 'right' || k === '+' || k === '=')) { e.preventDefault(); wsRef.current && (wsRef.current as any).skip?.(5) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [activeId, editing, meta])

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 overflow-x-hidden">
      <header className="sticky top-0 z-10 bg-white/80 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <img src="/localeyes_ltd_logo.jfif" alt="Company logo" className="h-6 w-auto mr-2 rounded-sm" />
          <h1 className="text-xl font-semibold">LE Annotator</h1>
          <div className="ml-auto flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-1 mr-2">
              <button onClick={togglePlay} disabled={!active} title="Play/Pause (Ctrl+Space)" className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-slate-200 bg-white text-slate-800 disabled:opacity-50">
                {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>
            </div>
            <button onClick={() => setShowHelp(true)} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 hover:bg-slate-100">
              <HelpCircle className="w-4 h-4" /> Help
            </button>
            <button onClick={runQA} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 hover:bg-slate-100">
              <RefreshCcw className="w-4 h-4" /> Run QA
            </button>
            <button
              onClick={async () => { if (activeId) { try { await saveTranscript(activeId) } catch { } } }}
              disabled={!active || !activeHasWriteHandle}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
              title={activeHasWriteHandle ? undefined : 'No write permission for this file'}
            >
              <Download className="w-4 h-4" /> Save to Disk
            </button>
            <button
              onClick={handleNextFile}
              disabled={!active || !activeHasWriteHandle}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
              title="Save, Check QA, and Next"
            >
              <ArrowRight className="w-4 h-4" /> Next File
            </button>
          </div>
        </div>
      </header>

      {writeWarning && (
        <div className="max-w-7xl mx-auto px-4 pt-3">
          <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-3 py-2 text-sm flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            <div>{writeWarning}</div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto grid md:grid-cols-[280px,1fr] gap-4 px-4 pt-2 pb-4">
        <aside className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm h-fit sticky top-[56px]">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold flex items-center gap-2"><FileText className="w-4 h-4" /> Files</h2>
            <span className="text-[11px] text-slate-500">Sorted A-Z</span>
          </div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600">
              <Filter className="w-3.5 h-3.5 text-slate-500" />
              <span>Only meeting=false</span>
            </div>
            <button
              type="button"
              onClick={() => setMeetingFalseOnly((prev) => !prev)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${
                meetingFalseOnly ? 'bg-sky-600' : 'bg-slate-300'
              }`}
              aria-pressed={meetingFalseOnly}
              title={meetingFalseOnly ? 'Show all files' : 'Show only meeting=false'}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${
                  meetingFalseOnly ? 'translate-x-4' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-700 mb-3">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50">Total {items.length}</span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700">Processed {processedCount}</span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-sky-200 bg-sky-50 text-sky-700">Pending {remainingCount}</span>
            {meetingFalseOnly && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-sky-200 bg-sky-50 text-sky-700">Shown {visibleItems.length}</span>
            )}
          </div>
          {meetingFalseOnly && !activeVisible && active && (
            <div className="mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 flex items-center justify-between gap-2">
              <span>Active file hidden by filter.</span>
              <button className="underline" onClick={() => setMeetingFalseOnly(false)}>Show all</button>
            </div>
          )}

          <div onClick={() => document.getElementById('drop-file-input')?.click()} onDrop={onDrop} onDragOver={(e) => e.preventDefault()} className="border border-dashed border-slate-300 rounded-xl p-4 text-center text-sm text-slate-500 mb-3 cursor-pointer">
            <div className="flex flex-col items-center gap-2">
              <UploadCloud className="w-5 h-5 text-slate-400" />
              <div>Click or drag-and-drop audio files or folders here (mp3, wav, m4a, flac, ogg). JSON transcripts inside folders are pulled in automatically.</div>
            </div>
            <input id="drop-file-input" type="file" accept=".mp3,.wav,.m4a,.flac,.ogg,.json,audio/*" multiple className="hidden" onChange={onFileInput} />
          </div>

          <ul className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
            {visibleItems.map((it) => {
              const isActive = activeId === it.id
              return (
                <li
                  key={it.id}
                  title={it.filename}
                  className={`relative p-3 pl-4 rounded-xl border text-sm cursor-pointer flex items-center gap-3 transition ${
                    isActive
                      ? "bg-sky-50 border-sky-300 shadow-[0_12px_32px_-18px_rgba(14,165,233,0.6)] ring-1 ring-sky-200"
                      : it.processed
                        ? "bg-emerald-50/70 border-emerald-200 text-slate-600"
                        : "border-slate-200 hover:bg-slate-50"
                  }`}
                  onClick={() => switchFile(it.id)}
                >
                  {isActive && <span className="absolute inset-y-2 left-2 w-1 rounded-full bg-sky-500" aria-hidden />}
                  <div className="flex-1 min-w-0">
                    <div className={`flex items-center gap-2 font-medium truncate ${it.processed ? "line-through decoration-emerald-500/80" : ""}`}>
                      <span className="truncate">{it.filename}</span>
                      {isActive && <span className="text-[11px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-200">Active</span>}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500 flex items-center gap-2">
                      <span>{durations[it.id] != null ? formatDurationShort(durations[it.id] as number) : "-"} | {getExt(it.filename) || "-"}</span>
                      {it.processed && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-[11px] font-medium">
                          <CheckCircle className="w-3 h-3" /> Processed
                        </span>
                      )}
                    </div>
                  </div>
                  <button className="p-1 rounded-lg hover:bg-slate-100" aria-label={`Remove ${it.filename}`} onClick={(e) => {
                    e.stopPropagation();
                    const ok = window.confirm(`Remove "${it.filename}" from the list?`);
                    if (!ok) return;
                    const next = items.filter(x => x.id !== it.id);
                    const sortedNext = sortItemsByName(next);
                    setItems(sortedNext);
                    if (activeId === it.id) {
                      setActiveId(sortedNext[0]?.id ?? null);
                    }
                  }}>
                    <Trash2 className="w-4 h-4 text-slate-400" />
                  </button>
                </li>
              )
            })}
            {!items.length && (
              <li className="text-xs text-slate-500">No files loaded.</li>
            )}
            {!!items.length && !visibleItems.length && (
              <li className="text-xs text-slate-500">No files match the filter.</li>
            )}
          </ul>
        </aside>

        <main className="space-y-4 min-w-0">
          <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 min-w-0 sticky top-[56px] z-20">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <button onClick={togglePlay} disabled={!active} className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-slate-900 text-white disabled:opacity-50">
                {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </button>
              <div className="text-xs text-slate-600 flex items-center gap-1"><Timer className="w-4 h-4" /> {toTimestamp(displayTime)} / {toTimestamp(activeDuration)}</div>
              {selectionRange ? (
                <div className="flex items-center gap-2 text-xs text-slate-700 px-2 py-1 rounded-lg border border-slate-200 bg-slate-50">
                  <Scissors className="w-4 h-4 text-sky-600" />
                  <span className="font-medium">Selection</span>
                  <span className="tabular-nums">{toTimestamp(selectionRange.start)}</span>
                  <span className="text-slate-400">-</span>
                  <span className="tabular-nums">{toTimestamp(selectionRange.end)}</span>
                  <span className="text-slate-500">({formatDurationShort(selectionDuration)})</span>
                </div>
              ) : (
                <div className="text-[11px] text-slate-500 px-2 py-1 rounded-lg border border-dashed border-slate-200 bg-slate-50">No region selected</div>
              )}
              <div className="ml-auto flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <span>Zoom</span>
                  <input type="range" min={0} max={200} value={zoom} onChange={(e) => { const v = Number(e.target.value); setZoom(v) }} />
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <span className="sr-only">Volume</span>
                  {volume > 0 ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                  <input type="range" min={0} max={1} step={0.01} value={volume} onChange={(e) => { const v = Number(e.target.value); setVolume(v); wsRef.current && (wsRef.current as any).setVolume(v) }} />
                </label>
                <div className="flex items-center gap-2 text-sm">
                  <span>Autoplay</span>
                  <button
                    type="button"
                    aria-pressed={autoplayEnabled}
                    onClick={() => setAutoplayEnabled(!autoplayEnabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full border transition ${
                      autoplayEnabled ? "bg-sky-600 border-sky-600" : "bg-slate-200 border-slate-300"
                    }`}
                    title="Start playback automatically when switching files"
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                        autoplayEnabled ? "translate-x-5" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
                <button
                  onClick={addRegionFromSelection}
                  disabled={!active || hasRegion}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 hover:bg-slate-100 disabled:bg-slate-100 disabled:text-slate-400 disabled:border-slate-200 disabled:cursor-not-allowed disabled:hover:bg-slate-100 disabled:opacity-50"
                >
                  <Scissors className="w-4 h-4" /> Create region
                </button>
                <button
                  onClick={clearRegions}
                  disabled={!active || !hasRegion}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 hover:bg-slate-100 disabled:bg-slate-100 disabled:text-slate-400 disabled:border-slate-200 disabled:cursor-not-allowed disabled:hover:bg-slate-100 disabled:opacity-50"
                >
                  Clear regions
                </button>
              </div>
            </div>
            <div className="relative w-full" style={{ contain: 'layout paint', maxWidth: '100%' }}>
              <div
                ref={scrollWrapRef}
                className="overflow-x-auto overflow-y-hidden"
                style={{ contain: 'layout paint', maxWidth: '100%', scrollbarGutter: 'stable both-edges' }}
                onScroll={(e) => {
                  const wrap = e.currentTarget as HTMLDivElement
                  try {
                    const sw = wrap.scrollWidth || 1
                    const cw = wrap.clientWidth || 1
                    const sl = wrap.scrollLeft || 0
                    setHasOverflowX(sw > cw)
                    const widthPct = Math.max(5, Math.min(100, (cw / sw) * 100))
                    const leftPct = Math.max(0, Math.min(100 - widthPct, (sl / sw) * 100))
                    setScrollThumbWidth(widthPct)
                    setScrollThumbLeft(leftPct)
                  } catch { }
                }}
              >
                <div ref={timelineEl} className="h-6" style={{ contain: 'layout paint', minWidth: '100%' }} />
                <div ref={wsEl} className="h-[72px]" style={{ contain: 'layout paint', minWidth: '100%' }} />
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 mt-8">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-slate-600 mr-2"><Tags className="w-4 h-4" /> Tags</div>
                <div className="hidden md:inline-flex items-center gap-2 text-xs text-slate-500"><Keyboard className="w-4 h-4" /> Ctrl+Space Play/Pause • Alt+I [inaudible] • Alt+S [Silence] • Alt+R Region • Alt+← / Alt+- -5s • Alt+→ / Alt++ +5s</div>
              </div>

              <div className="flex flex-wrap gap-2 mt-2">
                {DEFAULT_TAGS.filter(t => (FAVORITE_TAG_KEYS as readonly string[]).includes(t.key as string)).map((t) => (
                  <button key={t.key} onClick={() => addTag(t)} className="px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-sm">{t.label.replace('  ', ' ')}</button>
                ))}
              </div>
              <div className="mt-2 max-h-[20vh] overflow-auto pr-1">
                <div className="flex flex-wrap gap-2">
                  {DEFAULT_TAGS.filter(t => !(FAVORITE_TAG_KEYS as readonly string[]).includes(t.key as string)).map((t) => (
                    <button key={t.key} onClick={() => addTag(t)} className="px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-sm">{t.label.replace('  ', ' ')}</button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className={`grid gap-4 transition-all duration-300 ${isMetaCollapsed ? 'lg:grid-cols-[1fr,auto]' : 'lg:grid-cols-2'}`}>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold">Transcript</h3>
                <div className="flex items-center gap-2">
                  <label className="text-xs">
                    <span className="sr-only">Speaker</span>
                    <div className="flex items-center gap-2">
                      <select
                        className="px-2 py-1 rounded-lg border border-slate-200 text-sm"
                        value={selectedSpeakerIdx}
                        onChange={(e) => setSelectedSpeakerIdx(Number(e.target.value))}
                        disabled={!active || (meta[activeId as string]?.speakers?.length || 0) === 0}
                      >
                        {(meta[activeId as string]?.speakers || []).map((sp, idx) => (
                          <option key={idx} value={idx}>{sp.name || `Speaker ${idx + 1}`}</option>
                        ))}
                      </select>
                      <button onClick={() => insertSpeakerTag(selectedSpeakerIdx)} disabled={!active || (meta[activeId as string]?.speakers?.length || 0) === 0} className="px-2 py-1 rounded-lg border border-slate-200 text-sm inline-flex items-center gap-1">
                        <MoveVertical className="w-4 h-4" /> Insert
                      </button>
                    </div>
                  </label>
                </div>
              </div>
              <div className="relative min-h-[42vh] rounded-xl border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-sky-300 focus-within:border-sky-300">
                <pre className="pointer-events-none whitespace-pre-wrap break-words font-mono text-base leading-relaxed text-slate-900 min-h-[42vh] p-3 rounded-xl" aria-hidden="true">
                  {renderHighlightedTranscript(editing[activeId as string] || '')}
                </pre>
                <textarea
                  id="transcript-area"
                  className="absolute inset-0 w-full h-full p-3 font-mono text-base leading-relaxed bg-transparent text-transparent caret-slate-900 resize-none rounded-xl focus:outline-none"
                  value={editing[activeId as string] || ''}
                  onChange={(e) => setEditing({ ...editing, [activeId as string]: e.target.value })}
                  placeholder="Write the transcription here..."
                />
              </div>
            </div>

            <div className={`bg-white rounded-2xl border border-slate-200 shadow-sm p-3 space-y-3 transition-all duration-300 ${isMetaCollapsed ? 'w-[60px]' : 'w-full'}`}>
              <div className="flex items-center justify-between mb-1">
                {!isMetaCollapsed && <h3 className="font-semibold">Metadata</h3>}
                <button
                  onClick={() => setIsMetaCollapsed(!isMetaCollapsed)}
                  className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500 ml-auto"
                  title={isMetaCollapsed ? "Expand Metadata" : "Collapse Metadata"}
                >
                  {isMetaCollapsed ? <ChevronsLeft className="w-5 h-5" /> : <ChevronsRight className="w-5 h-5" />}
                </button>
              </div>

              {!isMetaCollapsed && active && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="text-sm">Filename
                      <input disabled value={active.filename} className="mt-1 w-full px-2 py-1 rounded-lg border border-slate-200 bg-slate-50" />
                    </label>
                    <label className="text-sm">Basename
                      <input disabled value={active.basename} className="mt-1 w-full px-2 py-1 rounded-lg border border-slate-200 bg-slate-50" />
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="text-sm">Language
                      <select className="mt-1 w-full px-2 py-1 rounded-lg border border-slate-200" value={meta[activeId as string]?.language || 'English'} onChange={(e) => {
                        const lang = e.target.value;
                        const allowed = (DIALECTS[lang] || []);
                        const m = structuredClone(meta);
                        const cur = m[activeId as string]
                          ? { ...m[activeId as string], speakers: padSpeakers(m[activeId as string].speakers) }
                          : createDefaultMeta();
                        cur.language = lang;
                        cur.speakers = padSpeakers((cur.speakers || []).map((s: any) => ({ ...s, dialect: allowed.includes(s.dialect || '') ? s.dialect : '' })));
                        m[activeId as string] = cur;
                        setMeta(m);
                      }}>
                        {LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
                      </select>
                    </label>
                    <label className="text-sm">Meeting
                      <select className="mt-1 w-full px-2 py-1 rounded-lg border border-slate-200" value={meta[activeId as string]?.meeting ? 'true' : 'false'} onChange={(e) => setMeta({ ...meta, [activeId as string]: { ...meta[activeId as string], meeting: e.target.value === 'true' } })}>
                        <option value="true">true</option>
                        <option value="false">false</option>
                      </select>
                    </label>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-medium text-sm">Speakers</div>
                      <button onClick={addSpeaker} className="inline-flex items-center gap-1 text-sky-700 hover:underline text-sm"><UserPlus className="w-4 h-4" /> Add</button>
                    </div>
                    <div className="space-y-2 max-h-[24vh] overflow-auto pr-1">
                      {meta[activeId as string]?.speakers?.map((sp, idx) => (
                        <div key={idx} className="grid grid-cols-[1.3fr,1fr,1fr,auto] gap-2 items-end">
                          <label className="text-xs">Name
                            <input value={sp.name} onChange={(e) => {
                              const m = structuredClone(meta)
                              m[activeId as string].speakers[idx].name = e.target.value; setMeta(m)
                            }} className="mt-1 w-full px-2 py-1 rounded-lg border border-slate-200" />
                          </label>
                          <label className="text-xs">Gender
                            <select value={sp.gender || ''} onChange={(e) => { const m = structuredClone(meta); m[activeId as string].speakers[idx].gender = e.target.value; setMeta(m) }} className="mt-1 w-full px-2 py-1 rounded-lg border border-slate-200">
                              <option value=""></option>
                              <option>Male</option>
                              <option>Female</option>
                              <option>Unidentified</option>
                            </select>
                          </label>
                          <label className="text-xs">Dialect
                            <select value={sp.dialect || ''} onChange={(e) => {
                              const m = structuredClone(meta);
                              const lang = m[activeId as string]?.language || 'English';
                              const allowed = (DIALECTS[lang] || []);
                              const next = allowed.includes(e.target.value) ? e.target.value : '';
                              m[activeId as string].speakers[idx].dialect = next;
                              setMeta(m);
                            }} className="mt-1 w-full px-2 py-1 rounded-lg border border-slate-200">
                              <option value=""></option>
                              {(DIALECTS[meta[activeId as string]?.language || 'English'] || []).map((d) => (
                                <option key={d} value={d}>{d}</option>
                              ))}
                            </select>
                          </label>
                          <div className="flex items-center justify-end pb-1">
                            <button
                              type="button"
                              onClick={() => insertSpeakerTag(idx)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 text-xs hover:bg-slate-100"
                              title="Insert this speaker tag into the transcript"
                            >
                              <MoveVertical className="w-4 h-4" /> Insert
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">Four speaker slots are preloaded; add more if you need them.</div>
                  </div>

                  <div className="text-xs text-slate-500">Tip: use the "Quick Tags" buttons or keyboard shortcuts to automatically insert tags and timestamps on the selected audio.</div>
                </>
              )}
              {!isMetaCollapsed && !active && (
                <div className="text-sm text-slate-500">Load and select a file to fill the metadata.</div>
              )}
            </div>
          </section>
          {qaOpen && qa && (
            <section ref={qaPanelRef} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">QA Results
                  <span className="ml-2 text-xs inline-flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded-full border border-red-200 text-red-700 bg-red-50">errors: {qa.counts.error}</span>
                    <span className="px-2 py-0.5 rounded-full border border-amber-200 text-amber-700 bg-amber-50">warnings: {qa.counts.warning}</span>
                    <span className="px-2 py-0.5 rounded-full border border-slate-200 text-slate-700 bg-slate-50">info: {qa.counts.info}</span>
                    <span className="px-2 py-0.5 rounded-full border border-sky-200 text-sky-700 bg-sky-50">total: {qa.counts.total}</span>
                  </span>
                </h3>
                <div className="flex items-center gap-2">
                  <button onClick={applyAutoFixes} className="px-2 py-1 rounded-lg border border-slate-200 text-sm hover:bg-slate-50">Auto-fix spacing/case</button>
                  <button onClick={runQA} className="px-2 py-1 rounded-lg border border-slate-200 text-sm hover:bg-slate-50">Re-run</button>
                  <button onClick={() => setQaOpen(false)} className="px-2 py-1 rounded-lg border border-slate-200 text-sm hover:bg-slate-50 inline-flex items-center gap-1"><X className="w-4 h-4" /> Close</button>
                </div>
              </div>
              <div className="max-h-[28vh] overflow-auto pr-1">
                <ul className="space-y-2">
                  {qa.issues.map((iss) => (
                    <li key={iss.id} className="p-2 rounded-lg border flex items-start gap-2 hover:bg-slate-50 cursor-pointer"
                      onClick={() => jumpToIssue(iss)}
                    >
                      <span className={
                        iss.severity === 'error' ? 'mt-0.5 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium text-red-700 bg-red-50 border border-red-200' :
                          iss.severity === 'warning' ? 'mt-0.5 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200' :
                            'mt-0.5 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium text-slate-700 bg-slate-50 border border-slate-200'
                      }>
                        {iss.severity}
                      </span>
                      <div className="text-sm">
                        <div className="font-medium">{iss.code}</div>
                        <div className="text-slate-600">{iss.message}</div>
                        {iss.sample ? <div className="mt-1 font-mono text-xs text-slate-500 truncate">{iss.sample}</div> : null}
                      </div>
                    </li>
                  ))}
                  {qa.issues.length === 0 && (
                    <li className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 p-2 rounded-lg inline-flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" /> No issues found.
                    </li>
                  )}
                </ul>
              </div>
              <div className="text-xs text-slate-500">Click an item to jump and select the problematic span in the transcript for quick editing.</div>
            </section>
          )}
        </main>
      </div>

      {
        showHelp && (
          <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowHelp(false)}>
            <div className="max-w-3xl w-full bg-white rounded-2xl shadow-xl p-4 sm:p-6 relative" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setShowHelp(false)} className="absolute top-3 right-3 p-1 rounded-full hover:bg-slate-100" aria-label="Close help">
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2 mb-3">
                <HelpCircle className="w-5 h-5 text-sky-600" /><h2 className="text-lg font-semibold">Help</h2>
              </div>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>Quick guide to annotation and verbatim transcription. Use Quick Tags to insert the required labels fast.</p>

                <div>
                  <div className="font-medium">Importing files</div>
                  <ul className="list-disc pl-5">
                    <li>Drag individual audio files or drop an entire folder; every supported audio plus any JSON inside the folder is imported automatically.</li>
                    <li>Audio is typically renamed with a 7-character batch prefix (e.g., A000005_). JSON files keep that prefix in the filename, while the <code>filename</code> field stored inside JSON refers to the original name without prefix/extension.</li>
                    <li>Files stay alphabetically sorted, and the counter above the drop area shows what is processed vs. pending.</li>
                    <li>Use the Autoplay toggle near the player to start the next file as soon as it loads.</li>
                  </ul>
                </div>

                <div>
                  <div className="font-medium">Tags and timestamps</div>
                  <ul className="list-disc pl-5">
                    <li>With timestamp (range): [inaudible], [unintelligible], [foreign Language], [foreign], [Silence], [crosstalk]. Select a region, otherwise a zero-length interval at the cursor is used.</li>
                    <li>No timestamp: [laughter], [clapping], [music], [noise], [singing], [attempt]…</li>
                    <li>Point-in-time crosstalk: [crosstalk] … [/crosstalk]. Use the dedicated button.</li>
                    <li>Speakers: insert [Speaker N] at the beginning of each turn. Add speakers in Metadata and insert them from the Transcript toolbar.</li>
                  </ul>
                </div>

                <div>
                  <div className="font-medium">Shortcuts</div>
                  <ul className="grid sm:grid-cols-2 gap-x-6 list-disc pl-5">
                    <li>Ctrl+Space → Play/Pause</li>
                    <li>Alt+I → [inaudible]</li>
                    <li>Alt+S → [Silence]</li>
                    <li>Alt+R → Quick 2s region around cursor</li>
                    <li>Alt+← / Alt+- → Skip -5 seconds</li>
                    <li>Alt+→ / Alt++ → Skip +5 seconds</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )
      }

      <footer className="max-w-7xl mx-auto px-4 pb-8 pt-4 text-xs text-slate-500 border-t border-slate-200 text-center">
        LE Transcriber by LocalEyes
      </footer>
    </div >
  )
}
